import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Paper,
  List,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { Send, Delete, ContentCopy } from "@mui/icons-material";
import {
  getMessages as apiGetMessages,
  createMessage as apiCreateMessage,
  deleteMessage as apiDeleteMessage,
} from "../../../services/api";
import ErrorMessage from "../errorMessage/ErrorMessage";

function formatDateLabel(dateStr) {
  const date = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const isSameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  if (isSameDay(date, today)) return "Today";
  if (isSameDay(date, yesterday)) return "Yesterday";

  return date.toLocaleDateString(undefined, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function getDateKey(dateStr) {
  const date = new Date(dateStr);
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

export default function Conversation({
  context,
  eventId,
  availabilityRequestId,
  isCaptainForTeam,
}) {
  const [inputText, setInputText] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [messages, setMessages] = useState([]);
  // const [gettingMessages, setGettingMessages] = React.useState(false);
  const [showDeleteMessage, setDeleteMessage] = React.useState(null);
  const [deletingMessage, setDeletingMessage] = React.useState(false);
  // const [deleteMessageError, setDeleteMessageError] = React.useState("");
  const [creatingMessage, setCreatingMessage] = React.useState(false);
  const [createMessageError, setCreateMessageError] = React.useState("");

  const bottomRef = useRef(null);

  const getMessages = useCallback(async () => {
    try {
      // setGettingMessages(true);
      const data = await apiGetMessages({ eventId, availabilityRequestId });
      if (data && !data.reason) {
        // Sort messages by date ascending (oldest first, newest at bottom)
        const sorted = [...data].sort(
          (a, b) => new Date(a.dateCreated) - new Date(b.dateCreated)
        );
        setMessages(sorted);
      }
      // setGettingMessages(false);
    } catch (e) {
      console.log(e);
      // setGettingMessages(false);
    }
  }, [eventId, availabilityRequestId]);

  useEffect(() => {
    getMessages();
  }, [getMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const createMessage = async (payload) => {
    try {
      setCreatingMessage(true);
      await apiCreateMessage(payload);
      await getMessages();
      setCreatingMessage(false);
    } catch (e) {
      console.log(e);
      setCreateMessageError("An error occurred confirming the event");
      setCreatingMessage(false);
    }
  };

  const deleteMessage = async () => {
    try {
      setDeletingMessage(true);
      await apiDeleteMessage(showDeleteMessage.id);
      setDeletingMessage(false);
      setDeleteMessage(null);
      getMessages();
    } catch (e) {
      setDeletingMessage(false);
      // setDeleteMessageError(e.message);
    }
  };

  const sendMessage = async () => {
    if (!inputText.trim()) return;

    await createMessage({
      eventId,
      availabilityRequestId,
      content: inputText.trim(),
      treasurerMessage: context.currentClub?.isTreasurer,
      captainMessage: isCaptainForTeam,
    });
    setInputText("");
  };

  const handleRightClick = (e, message) => {
    e.preventDefault();
    setSelectedMessage(message);
    setAnchorEl(e.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedMessage(null);
  };

  const handleCopyMessage = async () => {
    if (selectedMessage?.content) {
      await navigator.clipboard.writeText(selectedMessage.content);
    }
    handleCloseMenu();
  };

  const handleDeleteMessage = () => {
    if (selectedMessage) {
      deleteMessage({
        messageId: selectedMessage.id,
        eventId,
        availabilityRequestId,
      });
    }
    handleCloseMenu();
  };

  console.log("messages", messages);

  const renderMessageBubble = (message) => {
    const isUser = message.player?.guid === context.currentPlayer?.id;
    const canDelete = isUser || context.currentClub?.isTreasurer;
    const isAdmin = message.treasurerMessage || message.captainMessage;

    return (
      <Box
        onContextMenu={(e) => handleRightClick(e, { ...message, canDelete })}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: isUser ? "flex-end" : "flex-start",
          mb: 2,
          maxWidth: "80%",
          alignSelf: isUser ? "flex-end" : "flex-start",
        }}
      >
        {!isUser && message.player?.firstName && (
          <Typography
            variant="caption"
            sx={{ fontWeight: "bold", color: "#555", mb: 0.3 }}
          >
            {message.player.firstName} {message.player.lastName}
          </Typography>
        )}

        <Paper
          elevation={1}
          sx={{
            p: 1.5,
            borderRadius: 2,
            backgroundColor: isUser ? "#DCF8C6" : isAdmin ? "#E0EBFF" : "#fff",
            border: isAdmin || isUser ? "none" : "1px solid #E0EBFF",
          }}
        >
          {message.content && (
            <Typography
              variant="body2"
              sx={{
                color: isUser ? "#000" : isAdmin ? "#1A3E8E" : "#333",
              }}
            >
              {message.content}
            </Typography>
          )}
          {message.image && (
            <Box
              component="img"
              src={message.image}
              alt="message"
              sx={{
                width: 200,
                height: "auto",
                borderRadius: 1,
                mt: 1,
              }}
            />
          )}
        </Paper>
        <Typography
          variant="caption"
          sx={{ mt: 0.3, color: "#999", alignSelf: "flex-end" }}
        >
          {new Date(message.dateCreated).toLocaleTimeString()}
        </Typography>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: 500,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <List
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 2,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {messages?.map((message, index) => {
          const showDateSeparator =
            index === 0 ||
            getDateKey(message.dateCreated) !==
              getDateKey(messages[index - 1].dateCreated);

          return (
            <React.Fragment key={message.id}>
              {showDateSeparator && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    my: 2,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      height: "1px",
                      backgroundColor: "#e0e0e0",
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      backgroundColor: "#f0f0f0",
                      borderRadius: "12px",
                      color: "#666",
                      fontSize: "0.75rem",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatDateLabel(message.dateCreated)}
                  </Typography>
                  <Box
                    sx={{
                      flex: 1,
                      height: "1px",
                      backgroundColor: "#e0e0e0",
                    }}
                  />
                </Box>
              )}
              {renderMessageBubble(message)}
            </React.Fragment>
          );
        })}
        <Box ref={bottomRef} />
      </List>

      <Box
        sx={{
          borderTop: "1px solid #ddd",
          p: 1,
          display: "flex",
          alignItems: "center",
          backgroundColor: "#fff",
        }}
      >
        <TextField
          placeholder="Type a message..."
          fullWidth
          multiline
          maxRows={3}
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          sx={{
            "& .MuiInputBase-root": { borderRadius: "20px" },
            mr: 1,
          }}
        />
        <Tooltip title="Send message">
          <IconButton
            color="success"
            onClick={sendMessage}
            disabled={creatingMessage}
          >
            <Send />
          </IconButton>
        </Tooltip>
      </Box>
      {createMessageError && (
        <ErrorMessage message={createMessageError}></ErrorMessage>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={handleCopyMessage}>
          <ContentCopy fontSize="small" sx={{ mr: 1 }} />
          Copy Message
        </MenuItem>
        {selectedMessage?.canDelete && (
          <MenuItem
            onClick={() => !deletingMessage && handleDeleteMessage()}
            sx={{ color: "red" }}
          >
            <Delete fontSize="small" sx={{ mr: 1 }} />
            Delete Message
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
}
